import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box } from '@curves/ui-base';

import * as echarts from "echarts/core";
import { ScatterChart, PieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { transformJsonDataToChartData, getAgeRangeDisplayName, ageRangeMap } from "@Local/Grades/Components/gradesDataLoader";
import { scatterChartTooltipHTML, pieChartTooltipHTML } from "@Local/Grades/GradesChart/tooltip/tooltipUtils";
import { processPieChartData, processScatterChartData, createChartOption, chartBaseConfig } from "@Local/Grades/GradesChart/chartConfigs";
echarts.use([ScatterChart, PieChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);
const GradesChartBox = React.forwardRef((props, ref) => {
	const { tooltip = chartBaseConfig.tooltip, grid = chartBaseConfig.grid, xAxis = chartBaseConfig.xAxis, yAxis = chartBaseConfig.yAxis, style, others = {}, dark: darkProps } = props;
	let option = {
		title: chartBaseConfig.title,
		color: chartBaseConfig.color,
		legend: chartBaseConfig.legend,
		tooltip,
		grid,
		xAxis,
		yAxis,
		series: chartBaseConfig.series,
		...others,
	};
	const [chartInstance, setChartInstance] = useState(null);
	const [jsonData, setJsonData] = useState([]);
	const [selectedType, setSelectedType] = useState("");
	const [selectedAges, setSelectedAges] = useState([]);
	const [selectedClubs, setSelectedClubs] = useState([]);
	const [hasAgeFilter, setHasAgeFilter] = useState(false);
	const [hasClubFilter, setHasClubFilter] = useState(false);
	const [clubsData, setClubsData] = useState([]);
	const chartRef = useRef(null);
	const [ageGroupData, setAgeGroupData] = useState([]);
	const [selectedMonth, setSelectedMonth] = useState("");
	useImperativeHandle(ref, () => ({
		setSource: source => {},
		setType: type => {
			setSelectedType(type);
		},
		setApiData: data => {},
		setJsonData: data => {
			setJsonData(data || []);
		},
		setAgeRanges: ages => {
			setSelectedAges(ages);
			setHasAgeFilter(true);
		},
		setClubRanges: clubs => {
			setSelectedClubs(clubs);
			setHasClubFilter(true);
		},
		updateChartData: (data, type, ages, clubs, clubsData) => {
			setJsonData(data || []);
			setSelectedType(type);
			setSelectedAges(ages || []);
			setSelectedClubs(clubs || []);
			setHasAgeFilter(true);
			setHasClubFilter(true);
			if (clubsData && Array.isArray(clubsData)) {
				setClubsData(clubsData);
			}
		},
		triggerResize: (width, height) => {
			chartRef.current.style.width = width;
			chartRef.current.style.height = height;
			if (chartInstance) {
				chartInstance.resize();
			}
		},
		setAgeGroupData: data => {
			setAgeGroupData(data || []);
		},
		setMonth: month => {
			setSelectedMonth(month);
		},
	}));
	useEffect(
		() => {
			if (!chartInstance) return;
			if (!jsonData || jsonData.length === 0 || !selectedType) {
				return;
			}
			chartInstance.clear();
			const agesToUse = hasAgeFilter ? selectedAges : [];
			const clubsToUse = hasClubFilter ? selectedClubs : [];
			const transformedData = transformJsonDataToChartData(jsonData, selectedType, agesToUse, clubsToUse, clubsData);
			// 圓餅圖
			const pieData = processPieChartData(ageGroupData, selectedAges, hasAgeFilter, getAgeRangeDisplayName);
			// 散布圖
			const scatterSeries = processScatterChartData(transformedData, ageRangeMap, getAgeRangeDisplayName, scatterChartTooltipHTML);
			// 圖表選項
			const chartOption = createChartOption(option, selectedType, pieData, scatterSeries, pieChartTooltipHTML);
			chartInstance.setOption(chartOption);
		},
		// eslint-disable-next-line
		[chartInstance, selectedType, selectedAges, selectedClubs, hasAgeFilter, hasClubFilter, jsonData, selectedMonth, option, tooltip]
	);
	useEffect(
		() => {
			if (!chartRef.current) return;
			const instance = echarts.init(chartRef.current, darkProps ? "dark" : "light");
			setChartInstance(instance);
			return () => {
				if (instance) {
					instance.dispose();
				}
			};
		},
		// eslint-disable-next-line
		[darkProps]
	);
	return <Box ref={chartRef} style={style} />;
});
GradesChartBox.displayName = "GradesChartBox";
export default GradesChartBox;
